import { useEffect, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../ui';
import { useAdminApi } from '../../hooks/useAdminApi';

interface DashboardStats {
  contacts: { total: number; new: number; responded: number };
  demoRequests: { total: number; byStatus: Record<string, number>; recentCount: number; byIndustry: Record<string, number> };
  careers: { totalCareers: number; activeCareers: number; closedCareers: number; careersByDepartment: { department: string; count: number }[] };
  applications: { totalCandidates: number; totalApplications: number; applicationsByStatus: { status: string; count: number }[] };
}

interface ContactRequest {
  id: number; name: string; email: string; organization: string | null;
  inquiry_type: string | null; status: string; created_at: string; message: string | null;
}

interface DemoRequest {
  id: number; first_name: string; last_name: string; email: string;
  company_name: string; industry: string; status: string; priority: string; created_at: string;
}

interface Career {
  id: number; title: string; department: string; location: string;
  employment_type: string; status: string; created_at: string;
}

// Normalize status to lowercase for consistent color lookup
function statusDot(status: string): string {
  const colors: Record<string, string> = {
    new: 'bg-blue-500', contacted: 'bg-yellow-500', scheduled: 'bg-indigo-500',
    completed: 'bg-green-500', cancelled: 'bg-red-500', converted: 'bg-emerald-500',
    closed: 'bg-gray-400',
    applied: 'bg-blue-500', reviewed: 'bg-yellow-500', interviewed: 'bg-indigo-500',
    offered: 'bg-emerald-500', hired: 'bg-green-500', rejected: 'bg-red-500',
  };
  return colors[status.toLowerCase()] || 'bg-gray-400';
}

export default function AdminOverview() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [contacts, setContacts] = useState<ContactRequest[]>([]);
  const [demos, setDemos] = useState<DemoRequest[]>([]);
  const [careers, setCareers] = useState<Career[]>([]);
  const [loading, setLoading] = useState(true);
  const api = useAdminApi();
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;

    Promise.all([
      api.get('/api/admin/stats').then((res) => setStats(res.data)),
      api.get('/api/admin/contacts').then((res) => setContacts(res.data)),
      api.get('/api/admin/demo-requests').then((res) => setDemos(res.data)),
      api.get('/api/admin/careers').then((res) => setCareers(res.data)),
    ])
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) {
    return <div className="flex justify-center py-12 text-muted-foreground">Loading dashboard...</div>;
  }

  if (!stats) {
    return <div className="text-center py-12 text-red-500">Failed to load dashboard</div>;
  }

  const statCards = [
    { label: 'Contact Requests', value: stats.contacts.total, sub: `${stats.contacts.new} new`, color: 'bg-blue-50 text-blue-700', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
    { label: 'Demo Requests', value: stats.demoRequests.total, sub: `${stats.demoRequests.recentCount} this week`, color: 'bg-emerald-50 text-emerald-700', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { label: 'Active Careers', value: stats.careers.activeCareers, sub: `${stats.careers.totalCareers} total`, color: 'bg-purple-50 text-purple-700', icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
    { label: 'Applications', value: stats.applications.totalApplications, sub: `${stats.applications.totalCandidates} candidates`, color: 'bg-amber-50 text-amber-700', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
  ];

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s) => (
          <Card key={s.label} className="bg-white/80 backdrop-blur">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{s.label}</p>
                  <p className="text-3xl font-bold mt-1">{s.value}</p>
                  <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full ${s.color}`}>
                    {s.sub}
                  </span>
                </div>
                <div className={`p-3 rounded-full ${s.color}`}>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={s.icon} />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Two-column layout: Recent Contacts + Demo Requests */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Contacts */}
        <Card className="bg-white/80 backdrop-blur">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between">
              Recent Contacts
              <span className="text-xs font-normal text-muted-foreground">{contacts.length} total</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {contacts.slice(0, 6).map((c) => (
                <div key={c.id} className="flex items-start justify-between border-b border-border/50 pb-2 last:border-0">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full flex-shrink-0 ${statusDot(c.status)}`} />
                      <span className="text-sm font-medium truncate">{c.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground ml-4 truncate">{c.email}</p>
                    {c.message && (
                      <p className="text-xs text-muted-foreground ml-4 mt-0.5 line-clamp-1">{c.message}</p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <span className="text-xs capitalize text-muted-foreground">{c.inquiry_type?.replace('_', ' ') || '—'}</span>
                    <p className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
              {contacts.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No contacts yet</p>}
            </div>
          </CardContent>
        </Card>

        {/* Recent Demo Requests */}
        <Card className="bg-white/80 backdrop-blur">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between">
              Recent Demo Requests
              <span className="text-xs font-normal text-muted-foreground">{demos.length} total</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {demos.slice(0, 6).map((d) => (
                <div key={d.id} className="flex items-start justify-between border-b border-border/50 pb-2 last:border-0">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full flex-shrink-0 ${statusDot(d.status)}`} />
                      <span className="text-sm font-medium truncate">{d.first_name} {d.last_name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground ml-4 truncate">{d.company_name}</p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                      d.priority === 'high' || d.priority === 'urgent' ? 'bg-red-50 text-red-700' :
                      d.priority === 'medium' ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-600'
                    }`}>{d.priority}</span>
                    <p className="text-xs text-muted-foreground mt-0.5">{new Date(d.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
              {demos.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No demo requests yet</p>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Career Postings + Application Pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Careers */}
        <Card className="bg-white/80 backdrop-blur">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between">
              Career Postings
              <span className="text-xs font-normal text-muted-foreground">{careers.length} total</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {careers.map((c) => (
                <div key={c.id} className="flex items-center justify-between border-b border-border/50 pb-2 last:border-0">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full flex-shrink-0 ${statusDot(c.status)}`} />
                      <span className="text-sm font-medium truncate">{c.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground ml-4">{c.department} &middot; {c.location}</p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                      c.status.toLowerCase() === 'new' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>{c.status.toLowerCase() === 'new' ? 'Active' : 'Closed'}</span>
                    <p className="text-xs text-muted-foreground mt-0.5">{c.employment_type}</p>
                  </div>
                </div>
              ))}
              {careers.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No career postings yet</p>}
            </div>
          </CardContent>
        </Card>

        {/* Application Pipeline + Industry Breakdown */}
        <Card className="bg-white/80 backdrop-blur">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Pipeline & Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-4">
            {/* Application pipeline */}
            {stats.applications.applicationsByStatus.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Application Pipeline</p>
                <div className="flex flex-wrap gap-2">
                  {stats.applications.applicationsByStatus.map(({ status, count }) => (
                    <div key={status} className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5">
                      <span className={`h-2 w-2 rounded-full ${statusDot(status)}`} />
                      <span className="text-xs font-medium capitalize">{status.toLowerCase()}</span>
                      <span className="text-xs font-bold" style={{ color: '#1356a6' }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Demo requests by status */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Demo Request Status</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.demoRequests.byStatus).map(([status, count]) => (
                  <div key={status} className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5">
                    <span className={`h-2 w-2 rounded-full ${statusDot(status)}`} />
                    <span className="text-xs font-medium capitalize">{status.replace('_', ' ')}</span>
                    <span className="text-xs font-bold" style={{ color: '#1356a6' }}>{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Industry breakdown */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Demos by Industry</p>
              <div className="space-y-1.5">
                {Object.entries(stats.demoRequests.byIndustry).map(([industry, count]) => (
                  <div key={industry} className="flex items-center justify-between">
                    <span className="text-xs">{industry}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${(count / stats.demoRequests.total) * 100}%`, backgroundColor: '#1356a6' }}
                        />
                      </div>
                      <span className="text-xs font-medium w-4 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Departments */}
            {stats.careers.careersByDepartment.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Careers by Department</p>
                <div className="flex flex-wrap gap-2">
                  {stats.careers.careersByDepartment.map(({ department, count }) => (
                    <div key={department} className="rounded-lg border px-2.5 py-1.5 text-xs">
                      <span className="font-medium">{department}</span>
                      <span className="ml-1.5 font-bold" style={{ color: '#1356a6' }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
